import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  username?: string;
  displayName?: string;
  tier: 'guest' | 'free' | 'pro';
  emailVerified: boolean;
}

interface Usage {
  linksCreated: number;
  linksLimit: number;
  remainingLinks: number;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isGuest: boolean;
  usage: Usage | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, username?: string) => Promise<void>;
  logout: () => Promise<void>;
  initializeGuest: () => Promise<void>;
  convertGuestToUser: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshUsage: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  const isAuthenticated = !!user && user.tier !== 'guest';

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      setIsLoading(true);
      
      // Try to get authenticated user
      const token = localStorage.getItem('authToken');
      if (token) {
        const response = await api.get('/auth/me');
        if (response.data.user) {
          setUser(response.data.user);
          setUsage(response.data.usage);
          setIsGuest(false);
          return;
        }
      }

      // Check for guest session
      const guestId = getCookie('guest_id');
      if (guestId) {
        const response = await api.post('/auth/guest/init');
        if (response.data.guestIdentifier) {
          setUser({
            id: response.data.guestIdentifier,
            email: 'guest@anonlink.com',
            tier: 'guest',
            emailVerified: false
          });
          setUsage({
            linksCreated: response.data.linksCreated,
            linksLimit: response.data.maxLinks,
            remainingLinks: response.data.linksRemaining
          });
          setIsGuest(true);
        }
      }
    } catch (error) {
      console.error('Failed to load user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { user, token } = response.data;
      
      localStorage.setItem('authToken', token);
      setUser(user);
      setIsGuest(false);
      
      // Load usage data
      await refreshUsage();
      
      toast.success('Welcome back!');
    } catch (error: any) {
      const message = error.response?.data?.error || 'Login failed';
      toast.error(message);
      throw new Error(message);
    }
  };

  const register = async (email: string, password: string, username?: string) => {
    try {
      const response = await api.post('/auth/register', { 
        email, 
        password, 
        username 
      });
      const { user, token } = response.data;
      
      localStorage.setItem('authToken', token);
      setUser(user);
      setIsGuest(false);
      
      // Convert guest links if applicable
      const guestId = getCookie('guest_id');
      if (guestId) {
        try {
          await api.post('/auth/guest/convert');
          deleteCookie('guest_id');
        } catch (err) {
          console.error('Failed to convert guest session:', err);
        }
      }
      
      // Load usage data
      await refreshUsage();
      
      toast.success('Account created successfully!');
    } catch (error: any) {
      const message = error.response?.data?.error || 'Registration failed';
      toast.error(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      setUser(null);
      setUsage(null);
      setIsGuest(false);
      deleteCookie('session');
      toast.success('Logged out successfully');
    }
  };

  const initializeGuest = async () => {
    try {
      const response = await api.post('/auth/guest/init');
      
      setUser({
        id: response.data.guestIdentifier,
        email: 'guest@anonlink.com',
        tier: 'guest',
        emailVerified: false
      });
      
      setUsage({
        linksCreated: response.data.linksCreated,
        linksLimit: response.data.maxLinks,
        remainingLinks: response.data.linksRemaining
      });
      
      setIsGuest(true);
    } catch (error) {
      console.error('Failed to initialize guest session:', error);
      toast.error('Failed to start guest session');
    }
  };

  const convertGuestToUser = async () => {
    if (!isGuest || !user) return;
    
    try {
      await api.post('/auth/guest/convert');
      deleteCookie('guest_id');
      toast.success('Guest links transferred to your account!');
    } catch (error) {
      console.error('Failed to convert guest session:', error);
    }
  };

  const refreshUser = async () => {
    // Check if there's a token instead of isAuthenticated
    // This allows refreshing user data after login/registration
    const token = localStorage.getItem('authToken');
    if (!token) return;
    
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
      setUsage(response.data.usage);
      setIsGuest(false);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // If refresh fails, clear invalid token
      localStorage.removeItem('authToken');
      setUser(null);
    }
  };

  const refreshUsage = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await api.get('/auth/me');
      setUsage(response.data.usage);
    } catch (error) {
      console.error('Failed to refresh usage:', error);
    }
  }, [user]);

  // Helper functions for cookies
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  };

  const deleteCookie = (name: string) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        isGuest,
        usage,
        login,
        register,
        logout,
        initializeGuest,
        convertGuestToUser,
        refreshUser,
        refreshUsage
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};